import { TRANSITIONS, useSettings } from '../../store/settings';
import { Choice, Section, Toggle } from './controls';

// Slider positions map onto these durations: fine control at the short end, up to 60 min.
export const INTERVAL_STEPS = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 300, 600, 900, 1200, 1800, 2700, 3600];

export function formatInterval(sec: number): string {
  if (sec < 60) return `${sec} seconds`;
  const m = sec / 60;
  return m === 1 ? '1 minute' : `${m % 1 ? m.toFixed(1) : m} minutes`;
}

/** Settings → Appearance → Slideshow: timing, transitions, fit, quality and the shading overlay. */
export function SlideshowTab() {
  const media = useSettings((s) => s.media);
  const update = useSettings((s) => s.update);
  const stepIndex = INTERVAL_STEPS.reduce(
    (best, s, i) => (Math.abs(s - media.intervalSec) < Math.abs((INTERVAL_STEPS[best] ?? 0) - media.intervalSec) ? i : best),
    0,
  );

  return (
    <>
      <Section title="Slideshow">
        <label className="block">
          <div className="mb-1 flex justify-between text-sm">
            <span>Time per slide</span>
            <span className="muted">{formatInterval(media.intervalSec)}</span>
          </div>
          <input
            type="range"
            min={0}
            max={INTERVAL_STEPS.length - 1}
            value={stepIndex}
            onChange={(e) => update('media', { intervalSec: INTERVAL_STEPS[Number(e.target.value)] ?? 30 })}
            className="w-full accent-[var(--accent)]"
          />
        </label>
        <Choice options={TRANSITIONS} value={media.transition} onChange={(transition) => update('media', { transition })} />
        <Toggle label="Shuffle" checked={media.shuffle} onChange={(shuffle) => update('media', { shuffle })} />
        <Toggle
          label="Fill the screen (crops the edges of photos that don’t match the screen shape)"
          checked={media.fit === 'cover'}
          onChange={(fill) => update('media', { fit: fill ? 'cover' : 'contain' })}
        />
        <Toggle
          label="Only photos that match the screen’s shape (landscape photos on landscape layouts, portrait on portrait)"
          checked={media.matchOrientation}
          onChange={(matchOrientation) => update('media', { matchOrientation })}
        />
        <Toggle
          label="Demo video when no photos are chosen (otherwise the built-in ambient scenes)"
          checked={media.demo}
          onChange={(demo) => update('media', { demo })}
        />
      </Section>

      <Section
        title="Photo quality"
        description="For cloud photos. Original is sharpest on big 4K TVs; choose a lower size if photos load slowly."
      >
        <Choice
          options={[
            { id: 'original', name: 'Original', description: 'Full resolution' },
            { id: '4k', name: '4K', description: 'Up to 3840×2160' },
            { id: '1080', name: 'Full HD', description: 'Up to 1920×1080' },
          ]}
          value={media.quality}
          onChange={(quality) => update('media', { quality })}
          columns={3}
        />
      </Section>

      <Section title="Shading overlay" description="Tints the photos so widgets stay readable over bright pictures.">
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={media.overlayColor}
            onChange={(e) => update('media', { overlayColor: e.target.value })}
            className="h-10 w-14 cursor-pointer rounded border border-[var(--border)] bg-transparent"
            aria-label="Overlay colour"
          />
          <label className="flex-1">
            <div className="mb-1 flex justify-between text-sm">
              <span>Opacity</span>
              <span className="muted">{Math.round(media.overlayOpacity * 100)}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round(media.overlayOpacity * 100)}
              onChange={(e) => update('media', { overlayOpacity: Number(e.target.value) / 100 })}
              className="w-full accent-[var(--accent)]"
            />
          </label>
        </div>
      </Section>
    </>
  );
}
